import { Notification } from '../types';
import { mockNotifications, mockEvents } from './notificationData';

const STORAGE_KEY = 'mock_notifications';

export const getStoredNotifications = (): Notification[] => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : mockNotifications;
};

export const saveNotifications = (notifications: Notification[]) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(notifications));
};

export const handleCreateNotification = (variables: any): Notification => {
    const { data } = variables;
    const notifications = getStoredNotifications();

    const event = data.event || mockEvents[data.recordTypeId]?.find((e) => e.id === data.eventId);
    const recordType = data.recordType || {
        id: data.recordTypeId,
        name: data.recordTypeId,
        displayName: data.recordTypeId
    };

    if (!event) {
        throw new Error('Invalid event');
    }

    const isDuplicate = notifications.some(
        (n) =>
            n.recordType.id === recordType.id && n.event.id === event.id && JSON.stringify(n.condition) === JSON.stringify(data.condition)
    );

    if (isDuplicate) {
        throw new Error('A notification with the same configuration already exists');
    }

    const newNotification: Notification = {
        id: `n${Date.now()}`,
        name: data.name || `Notification ${Date.now()}`,
        recordType,
        event,
        condition: data.applyCondition ? data.condition : undefined,
        priority: data.priority,
        frequency: data.frequency,
        channels: ['in_app'],
        enabled: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
    };

    const updated = [...notifications, newNotification];
    saveNotifications(updated);

    return newNotification;
};

export const handleUpdateNotification = (variables: any): Notification => {
    const { id, data } = variables;
    const notifications = getStoredNotifications();
    const index = notifications.findIndex((n) => n.id === id);

    if (index === -1) {
        throw new Error('Notification not found');
    }

    const notification = notifications[index];

    if (data.name !== undefined) notification.name = data.name;
    if (data.priority !== undefined) notification.priority = data.priority;
    if (data.frequency !== undefined) notification.frequency = data.frequency;
    if (data.applyCondition !== undefined) {
        notification.condition = data.applyCondition ? data.condition : undefined;
    }

    notification.updatedAt = new Date().toISOString();

    notifications[index] = notification;
    saveNotifications(notifications);

    return notification;
};

export const handleDeleteNotification = (variables: any): Notification => {
    const { id } = variables;
    const notifications = getStoredNotifications();
    const notification = notifications.find((n) => n.id === id);

    if (!notification) {
        throw new Error('Notification not found');
    }

    // Soft delete: marcar como deleted en lugar de eliminar
    notification.deleted = true;
    notification.deletedAt = new Date().toISOString();
    notification.updatedAt = new Date().toISOString();

    saveNotifications(notifications);
    return notification;
};

export const handleRestoreNotification = (variables: any): Notification => {
    const { id } = variables;
    const notifications = getStoredNotifications();
    const notification = notifications.find((n) => n.id === id);

    if (!notification) {
        throw new Error('Notification not found');
    }

    if (!notification.deleted) {
        throw new Error('Notification is not deleted');
    }

    // Restaurar notificación
    notification.deleted = false;
    delete notification.deletedAt;
    notification.updatedAt = new Date().toISOString();

    saveNotifications(notifications);
    return notification;
};

export const handleToggleNotification = (variables: any): Notification => {
    const { id, enabled } = variables;
    const notifications = getStoredNotifications();
    const notification = notifications.find((n) => n.id === id);

    if (!notification) {
        throw new Error('Notification not found');
    }

    notification.enabled = enabled;
    notification.updatedAt = new Date().toISOString();

    saveNotifications(notifications);
    return notification;
};

export const handleBulkToggle = (variables: any): Notification[] => {
    const { enabled } = variables;
    const notifications = getStoredNotifications();

    notifications.forEach((n) => {
        n.enabled = enabled;
        n.updatedAt = new Date().toISOString();
    });

    saveNotifications(notifications);
    return notifications;
};
